import { lazy, type ReactElement } from 'react'
import { Navigate, Route } from 'react-router-dom'
import Layout from '~/components/Layout/Layout'

const HomePage = lazy(() => import('~/pages/Home'))
const AboutUsPage = lazy(() => import('~/pages/AboutUs'))
const BenchPage = lazy(() => import('~/pages/Bench'))
const BlogIndexPage = lazy(() => import('~/pages/Blog'))
const BlogColorsPage = lazy(() => import('~/pages/blog/Colors'))
const BlogMeasurementsPage = lazy(() => import('~/pages/blog/Measurements'))
const CartRoutePage = lazy(() => import('~/pages/Cart'))
const CatalogRoutePage = lazy(() => import('~/pages/Catalog'))
const CheckoutPage = lazy(() => import('~/pages/Checkout'))
const ContactsPage = lazy(() => import('~/pages/Contacts'))
const BookVisitRoutePage = lazy(() => import('~/pages/contact/BookVisit'))
const FaqPage = lazy(() => import('~/pages/Faq'))
const GreenWallPage = lazy(() => import('~/pages/GreenWall'))
const OfficeTablePage = lazy(() => import('~/pages/OfficeTable'))
const OrderThankYouRoutePage = lazy(() => import('~/pages/order/ThankYou'))
const SamplesRoutePage = lazy(() => import('~/pages/product/Samples'))
const ProductsPage = lazy(() => import('~/pages/Products'))
const ReviewsPage = lazy(() => import('~/pages/Reviews'))
const StoolPage = lazy(() => import('~/pages/Stool'))
const TablePage = lazy(() => import('~/pages/Table'))
const TermsPage = lazy(() => import('~/pages/Terms'))
const WoodenRackPage = lazy(() => import('~/pages/WoodenRack'))

const GreenWallConfiguratorPage = lazy(
  () => import('~/pages/configurator/GreenWall')
)
const OfficeTableConfiguratorPage = lazy(
  () => import('~/pages/configurator/OfficeTable')
)
const TableConfiguratorPage = lazy(() => import('~/pages/configurator/Table'))
const WorkTableConfiguratorPage = lazy(
  () => import('~/pages/configurator/WorkTable')
)
const DynamicConfiguratorPage = lazy(() => import('~/pages/configurator/Dynamic'))

const BedsidePreview1 = lazy(() => import('~/pages/product/bedside/1'))
const BedsidePreview2 = lazy(() => import('~/pages/product/bedside/2'))
const StandPreview1 = lazy(() => import('~/pages/product/stand/1'))
const StandPreview2 = lazy(() => import('~/pages/product/stand/2'))
const TVStandPreview1 = lazy(() => import('~/pages/product/tv-stand/1'))
const TVStandPreview2 = lazy(() => import('~/pages/product/tv-stand/2'))
const WardrobePreview1 = lazy(() => import('~/pages/product/wardrobe/1'))
const WardrobePreview2 = lazy(() => import('~/pages/product/wardrobe/2'))
const WardrobePreview3 = lazy(() => import('~/pages/product/wardrobe/3'))
const WardrobePreview4 = lazy(() => import('~/pages/product/wardrobe/4'))

/**
 * Inner route set that lives under the Layout. Mounted twice:
 *   - at "/"   for the default locale (ro)
 *   - at "/ru" for the Russian locale
 * The locale is derived from the current pathname in IntlProviderShell.
 */
function inner(): ReactElement[] {
  return [
    <Route key="index" index element={<HomePage />} />,
    <Route key="about-us" path="about-us" element={<AboutUsPage />} />,
    <Route key="bench" path="bench" element={<BenchPage />} />,
    <Route key="blog" path="blog" element={<BlogIndexPage />} />,
    <Route key="blog-colors" path="blog/colors" element={<BlogColorsPage />} />,
    <Route
      key="blog-measurements"
      path="blog/measurements"
      element={<BlogMeasurementsPage />}
    />,
    <Route key="cart" path="cart" element={<CartRoutePage />} />,
    <Route key="catalog" path="catalog" element={<CatalogRoutePage />} />,
    <Route key="checkout" path="checkout" element={<CheckoutPage />} />,
    <Route key="contacts" path="contacts" element={<ContactsPage />} />,
    <Route
      key="contact-book-visit"
      path="contact/book-visit"
      element={<BookVisitRoutePage />}
    />,
    <Route key="faq" path="faq" element={<FaqPage />} />,
    <Route key="greenwall" path="greenwall" element={<GreenWallPage />} />,
    <Route key="office-table" path="office-table" element={<OfficeTablePage />} />,
    <Route
      key="order-thank-you"
      path="order/thank-you"
      element={<OrderThankYouRoutePage />}
    />,
    <Route
      key="product-samples"
      path="product/samples"
      element={<SamplesRoutePage />}
    />,
    <Route key="products" path="products" element={<ProductsPage />} />,
    <Route key="reviews" path="reviews" element={<ReviewsPage />} />,
    <Route key="stool" path="stool" element={<StoolPage />} />,
    <Route key="table" path="table" element={<TablePage />} />,
    <Route key="terms" path="terms" element={<TermsPage />} />,
    <Route key="wooden-rack" path="wooden-rack" element={<WoodenRackPage />} />,

    <Route
      key="configurator-greenwall"
      path="configurator/greenwall"
      element={<GreenWallConfiguratorPage />}
    />,
    <Route
      key="configurator-office-table"
      path="configurator/office-table"
      element={<OfficeTableConfiguratorPage />}
    />,
    <Route
      key="configurator-table"
      path="configurator/table"
      element={<TableConfiguratorPage />}
    />,
    <Route
      key="configurator-work-table"
      path="configurator/work-table"
      element={<WorkTableConfiguratorPage />}
    />,
    <Route
      key="configurator-dynamic"
      path="configurator/:product"
      element={<DynamicConfiguratorPage />}
    />,

    <Route
      key="product-bedside-1"
      path="product/bedside/1"
      element={<BedsidePreview1 />}
    />,
    <Route
      key="product-bedside-2"
      path="product/bedside/2"
      element={<BedsidePreview2 />}
    />,
    <Route key="product-stand-1" path="product/stand/1" element={<StandPreview1 />} />,
    <Route key="product-stand-2" path="product/stand/2" element={<StandPreview2 />} />,
    <Route
      key="product-tv-stand-1"
      path="product/tv-stand/1"
      element={<TVStandPreview1 />}
    />,
    <Route
      key="product-tv-stand-2"
      path="product/tv-stand/2"
      element={<TVStandPreview2 />}
    />,
    <Route
      key="product-wardrobe-1"
      path="product/wardrobe/1"
      element={<WardrobePreview1 />}
    />,
    <Route
      key="product-wardrobe-2"
      path="product/wardrobe/2"
      element={<WardrobePreview2 />}
    />,
    <Route
      key="product-wardrobe-3"
      path="product/wardrobe/3"
      element={<WardrobePreview3 />}
    />,
    <Route
      key="product-wardrobe-4"
      path="product/wardrobe/4"
      element={<WardrobePreview4 />}
    />,

    <Route key="not-found" path="*" element={<Navigate to="/" replace />} />,
  ]
}

export function AppRoutes() {
  return (
    <>
      <Route element={<Layout />}>{inner()}</Route>
      <Route path="ru" element={<Layout />}>
        {inner()}
      </Route>
      {/* Cloudflare strips "/ro", but support it in dev too */}
      <Route path="ro" element={<Layout />}>
        {inner()}
      </Route>
    </>
  )
}
